import { Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';

import { Store } from '@ngrx/store';
import { UpdatePointCustomField } from 'src/app/project/modules/points/points.actions';

import { TPoint } from 'src/app/project/modules/points/points.model';

import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { SitePointFilterService } from 'src/app/project/modules/filters/site-point-filter.service';
import { PointsUpdateService } from 'src/app/project/modules/points/points-update.service';
import { PointActivityService } from '../../point-timeline/point-activity.service';
import { PointFieldsService } from '../point-fields.service';

import { CustomFieldListSelectDropdownComponent } from 'src/app/project/modules/custom-fields/custom-field-list-select-dropdown/custom-field-list-select-dropdown.component';

import { TPointUpdate } from '@project/view-models';
import { of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import {
  EIntegrationStatus,
  TCustomFieldList,
} from 'src/app/project/modules/custom-fields/custom-fields.model';
import { TPointCustomField } from 'src/app/project/view-models/custom-field-response-model';
import { EIconPath } from '../../../../../shared/enums/icons.enum';
import { iconMap } from 'src/app/project/shared/enums/icon-map';

@Component({
  selector: 'pp-point-fields-list',
  templateUrl: './point-fields-list.component.html',
  styleUrls: ['./point-fields-list.component.scss', '../point-fields.component.scss'],
  standalone: false,
})
export class PointFieldsListComponent implements OnDestroy {
  @Input() ppField: TPointCustomField;
  @Input() ppWorkspaceId: string;
  @Input() ppPointId: string;
  @Input() ppFieldId: string;
  @Input() ppFieldLabel: string;
  @Input() ppFieldValue: string;
  @Input() ppFieldValueId: string;
  @Input() ppNew: boolean;
  @Input() ppCanEdit: boolean;
  @Input() ppVolyActive: EIntegrationStatus;
  @Input() ppFieldDescription: string;
  @Input() ppIsLocked: boolean;

  @ViewChild('pointFieldsList') pointFieldsListElement: ElementRef;

  private readonly destroy$ = new Subject<void>();

  updating = false;
  error = false;
  focused = false;
  EIconPath = EIconPath;
  iconMap = iconMap;
  EIntegrationStatus = EIntegrationStatus;

  dropdown = this.dropdownService.getDropdown();
  buttonId: string;

  constructor(
    private store: Store<{ points: TPoint[] }>,
    private dropdownService: DropdownService,
    private promptService: PromptService,
    private pointFieldsService: PointFieldsService,
    private pointActivityService: PointActivityService,
    private sitePointFilterService: SitePointFilterService,
    private pointsUpdateService: PointsUpdateService,
    private translationPipe: TranslationPipe,
  ) {}

  ngOnChanges(): void {
    this.buttonId = `clist${this.ppFieldId}`;
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  showList(): void {
    if (!this.ppCanEdit || this.ppIsLocked) {
      return;
    }

    if (this.dropdown.visible && this.dropdown.buttonId === this.buttonId) {
      this.hideListDropdown();
    } else {
      this.showListDropdown();
    }
  }

  clearCustomField(): void {
    this.ppFieldValue = null;
    this.updateField();
  }

  selectCustomField(itemId: string, path: string): void {
    this.ppFieldValue = path;
    this.ppFieldValueId = itemId;
    this.updateField();
  }

  deselectCustomField(): void {
    this.ppFieldValue = null;
    this.ppFieldValueId = null;
    this.updateField();
  }

  updateField(): void {
    const _id = this.ppPointId;

    if (this.ppNew) {
      this.updateFieldForNewPoint(
        this.ppPointId,
        this.ppFieldValue,
        this.ppFieldValueId,
        this.ppWorkspaceId,
        this.ppFieldId,
      );

      return;
    }

    this.updating = true;

    const body: TPointUpdate = {
      customFieldsList: [
        {
          customFieldTemplateId: this.ppFieldId,
          value: this.ppFieldValueId,
        },
      ],
    };

    this.pointsUpdateService
      .updatePointField(_id, body)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          const promptText = this.translationPipe.transform('prompt_point_list_update');

          this.sitePointFilterService.filterPoints({ _keepScrollPosition: true });
          this.promptService.showSuccess(promptText);
          this.pointActivityService.refreshTimeline(this.ppWorkspaceId, _id);
        }),
        catchError((error) => {
          this.pointFieldsService.showUpdatePointFieldError(error);

          this.error = true;

          return of();
        }),
        finalize(() => {
          this.updating = false;
        }),
      )
      .subscribe();
  }

  arrowClicked(): void {
    if (!this.ppCanEdit) {
      return;
    }

    this.showList();
  }

  private updateFieldForNewPoint(
    pointId: string,
    fieldValue: string,
    fieldValueId: string,
    workspaceId: string,
    customFieldId: string,
  ): void {
    this.store.dispatch(
      new UpdatePointCustomField({
        workspaceId: workspaceId,
        pointId: pointId,
        customFieldId: customFieldId,
        customFieldValue: fieldValue,
        customFieldValueId: fieldValueId,
      }),
    );
  }

  private showListDropdown(): void {
    this.focused = true;

    const inputElement = this.pointFieldsListElement.nativeElement.getBoundingClientRect();

    this.dropdownService.setData({
      field: this.ppField,
      selected: this.ppFieldValueId,
    });

    this.dropdownService.showDropdown(this.buttonId, CustomFieldListSelectDropdownComponent, {
      callback: (item) => {
        this.onListSelectDropdownCallback(item);
      },
      onClose: () => {
        this.onListSelectDropdownClose();
      },
      suppressScrollbar: true,
      width: `${Math.max(225, inputElement.width)}px`,
    });
  }

  private onListSelectDropdownClose(): void {
    this.focused = false;
  }

  private onListSelectDropdownCallback(item: TCustomFieldList): void {
    if (item) {
      this.selectCustomField(item.id, item.path);
    } else {
      this.deselectCustomField();
    }

    this.focused = false;
  }

  private hideListDropdown(): void {
    this.focused = false;

    this.dropdownService.hideDropdown();
  }
}
